import React, { useState, useEffect, useCallback } from 'react'
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import NotificationCenter from '../NotificationCenter'
import QuickActions from '../QuickActions'
import { RoleBasedAIChatbot, RoleBasedChatbotTrigger } from '../AI/RoleBasedAIChatbot'
import UserAvatar from '../UserAvatar'
import ScrollToTop from '../ui/ScrollToTop'
import { 
  HomeIcon, 
  BookOpenIcon, 
  UsersIcon, 
  CalendarIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  CpuChipIcon,
  AcademicCapIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { Brain } from 'lucide-react'

// 导航项类型
interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

const FULL_WIDTH_PATHS = ['/student/dashboard']

const Layout = () => {
  const { user, logout, clearAll } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [isChatbotOpen, setIsChatbotOpen] = useState(false)
  const [isSimpleChatOpen, setIsSimpleChatOpen] = useState(false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  // 检查是否在AI页面，如果是则不显示全局聊天触发器
  const isAIPage = location.pathname.includes('/ai')
  const isFullWidthRoute = FULL_WIDTH_PATHS.some((path) =>
    location.pathname.startsWith(path)
  )
  const contentWrapperClass = isFullWidthRoute
    ? 'mx-auto w-full px-2 sm:px-4 md:px-6'
    : 'max-w-7xl mx-auto px-3 sm:px-6 md:px-8'

  // 关闭移动端侧边栏
  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false)
  }, [])

  // 路由变化时关闭移动端侧边栏
  useEffect(() => {
    closeMobileSidebar()
  }, [location.pathname, closeMobileSidebar])

  // 锁定背景滚动
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [isMobileSidebarOpen])

  // 检查认证状态一致性
  useEffect(() => {
    if (!user) {
      console.log('Layout: 用户信息缺失，清除状态并刷新页面')
      clearAll()
      window.location.reload()
    }
  }, [user, clearAll])

  const handleLogout = () => {
    logout()
    navigate('/login')
    closeMobileSidebar()
  }

  // 根据用户角色设置导航
  let navigation: NavigationItem[] = []
  
  if (user?.role === 'teacher') {
    navigation = [
      { name: '仪表板', href: '/teacher/dashboard', icon: HomeIcon },
      { name: 'AI助手', href: '/teacher/ai', icon: Brain },
      { name: '课程', href: '/courses', icon: BookOpenIcon },
      { name: '老师', href: '/teachers', icon: UsersIcon },
      { name: '我的课程', href: '/teacher/courses', icon: BookOpenIcon },
      { name: '我的预约', href: '/teacher/bookings', icon: CalendarIcon },
      { name: '消息中心', href: '/teacher/messages', icon: ChatBubbleLeftRightIcon },
      { name: '课程安排', href: '/teacher/schedule', icon: CalendarIcon },
      { name: '评分异议', href: '/teacher/rating-disputes', icon: ExclamationTriangleIcon },
      { name: '个人资料', href: '/teacher/profile', icon: UserIcon },
    ]
  } else if (user?.role === 'admin') {
    navigation = [
      { name: '仪表板', href: '/admin/dashboard', icon: HomeIcon },
      { name: 'AI助手', href: '/admin/ai', icon: Brain },
      { name: 'AI招生官', href: '/admin/ai-admission', icon: CpuChipIcon },
      { name: '课程', href: '/courses', icon: BookOpenIcon },
      { name: '老师', href: '/teachers', icon: UsersIcon },
      { name: '用户管理', href: '/admin/users', icon: UsersIcon },
      { name: '消息监控', href: '/admin/messages', icon: ChatBubbleLeftRightIcon },
      { name: '教师管理', href: '/admin/teachers', icon: UsersIcon },
      { name: '教师申请管理', href: '/admin/teacher-applications', icon: DocumentTextIcon },
      { name: '课程管理', href: '/admin/courses', icon: BookOpenIcon },
      { name: '预约管理', href: '/admin/bookings', icon: CalendarIcon },
      { name: '学生成绩管理', href: '/admin/achievements', icon: TrophyIcon },
      { name: '评分异议管理', href: '/admin/rating-disputes', icon: ExclamationTriangleIcon },
    ]
  } else {
    // 学生导航
    navigation = [
      { name: '仪表板', href: '/student/dashboard', icon: HomeIcon },
      { name: 'AI助手', href: '/student/ai', icon: Brain },
      { name: '考试中心', href: '/student/exams', icon: AcademicCapIcon },
      { name: '知识库', href: '/student/knowledge', icon: BookOpenIcon },
      { name: '错题本', href: '/student/wrong-answers', icon: DocumentTextIcon },
      { name: '学习分析', href: '/student/learning-analysis', icon: ChartBarIcon },
      { name: '入学申请', href: '/student/admission-application', icon: DocumentTextIcon },
      { name: '课程', href: '/courses', icon: BookOpenIcon },
      { name: '群组课程', href: '/student/group-courses', icon: UsersIcon },
      { name: '老师', href: '/teachers', icon: UsersIcon },
      { name: '我的预约', href: '/student/bookings', icon: CalendarIcon },
      { name: '消息中心', href: '/student/messages', icon: ChatBubbleLeftRightIcon },
      { name: '个人资料', href: '/student/profile', icon: UserIcon },
    ]
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between h-14 sm:h-16">
            {/* 左侧：移动端菜单按钮 + Logo */}
            <div className="flex items-center">
              {/* 移动端菜单按钮 */}
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="md:hidden p-2 -ml-1 mr-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                aria-label="打开菜单"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => navigate('/')}>
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-blue-600 flex items-center justify-center mr-2">
                  <span className="text-lg sm:text-xl">🎓</span>
                </div>
                <h1 className="text-base sm:text-lg font-bold text-gray-900 hidden xs:block">云庠教育</h1>
              </div>
            </div>
            
            {/* 右侧：通知、用户信息、退出 */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <NotificationCenter />
              
              {/* 用户信息 - 桌面端显示 */}
              <div className="hidden sm:flex items-center space-x-3">
                <span className="text-sm text-gray-700 max-w-[120px] truncate">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full whitespace-nowrap">
                  {user?.role === 'admin' ? '管理员' : user?.role === 'teacher' ? '教师' : '学生'}
                </span>
              </div>
              
              {/* 退出按钮 - 桌面端显示 */}
              <button
                onClick={handleLogout}
                className="hidden sm:inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
                退出
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 移动端侧边栏遮罩 */}
      <div 
        className={`md:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-all duration-300 ease-out ${
          isMobileSidebarOpen 
            ? 'opacity-100 visible' 
            : 'opacity-0 invisible pointer-events-none'
        }`} 
        onClick={closeMobileSidebar}
        aria-hidden={!isMobileSidebarOpen}
      />

      {/* 移动端侧边栏 */}
      <div 
        className={`md:hidden fixed top-0 left-0 bottom-0 w-[280px] max-w-[85vw] bg-white z-[70] transform transition-transform duration-300 ease-out shadow-2xl flex flex-col ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ 
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)'
        }}
        role="dialog"
        aria-modal="true"
        aria-label="导航菜单"
      >
        {/* 移动端侧边栏头部 */}
        <div className="flex-shrink-0 p-4 flex justify-between items-center border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center space-x-2">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <span className="text-lg">🎓</span>
            </div>
            <span className="text-base font-bold text-gray-900">云庠教育</span>
          </div>
          <button 
            onClick={closeMobileSidebar} 
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 active:scale-95 transition-all"
            aria-label="关闭菜单"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 用户信息 */}
        <div className="flex-shrink-0 p-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <UserAvatar
              avatar={user?.avatar || undefined}
              firstName={user?.firstName || undefined}
              lastName={user?.lastName || undefined}
              size="md"
              gradient
              shadow
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <span className="flex-shrink-0 text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
              {user?.role === 'admin' ? '管理员' : user?.role === 'teacher' ? '教师' : '学生'}
            </span>
          </div>
        </div>
        
        {/* 移动端导航链接 */}
        <div className="flex-1 overflow-y-auto py-3 px-3 space-y-1 overscroll-contain">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/dashboard' && 
               location.pathname.startsWith(item.href + '/'));
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 active:bg-gray-100'
                }`}
                onClick={closeMobileSidebar}
              >
                <item.icon className={`mr-3 h-5 w-5 flex-shrink-0 ${
                  isActive ? 'text-blue-500' : 'text-gray-400'
                }`} />
                <span className="flex-1">{item.name}</span>
                {isActive && (
                  <ChevronRightIcon className="w-4 h-4 text-blue-400" />
                )}
              </Link>
            );
          })}
        </div>

        {/* 移动端底部退出按钮 */}
        <div 
          className="flex-shrink-0 p-4 border-t border-gray-100 bg-gray-50"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)' }}
        >
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 active:scale-[0.98] transition-all"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2 text-gray-400" />
            退出登录
          </button>
        </div>
      </div>

      <div className="flex flex-1">
        {/* 桌面端侧边栏 */}
        <div className="hidden md:flex md:w-56 lg:w-64 md:flex-col flex-shrink-0">
          <div className="flex flex-col flex-grow pt-4 bg-white border-r border-gray-200 overflow-y-auto">
            <nav className="flex-1 px-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/dashboard' && 
                   location.pathname.startsWith(item.href + '/'));
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border-l-3 border-blue-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`} />
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* 主内容区域 */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <main className="flex-1 relative overflow-y-auto focus:outline-none h-[calc(100vh-56px)] sm:h-[calc(100vh-64px)]">
            <div className="py-4 sm:py-6">
              <div className={contentWrapperClass}>
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* 浮动操作区域 - 避免与页面内容重叠 */}
      <div className="fixed bottom-0 left-0 right-0 pointer-events-none z-30">
        {/* 移动端底部安全区域 */}
        <div 
          className="h-20 sm:h-24 bg-gradient-to-t from-gray-50/80 to-transparent md:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        />
      </div>

      {/* 快捷操作 - 调整移动端位置 */}
      <div className="hidden sm:block">
        <QuickActions />
      </div>

      {/* 全局AI聊天助手 - 只在非AI页面显示，根据用户角色定制 */}
      {!isAIPage && (
        <>
          <RoleBasedChatbotTrigger onClick={() => setIsChatbotOpen(true)} />
          <RoleBasedAIChatbot 
            isOpen={isChatbotOpen} 
            onClose={() => setIsChatbotOpen(false)} 
          />
        </>
      )}

      {/* 滚动到顶部按钮 */}
      <ScrollToTop />
      
    </div>
  )
}

export default Layout 